import {
  AwardIcon,
  BellIcon,
  CheckCircle2Icon,
  ClockIcon,
  CompassIcon,
  CreditCardIcon,
  FileWarningIcon,
  MapIcon,
  SettingsIcon,
  ShieldAlertIcon,
  UserCheckIcon,
  UserPlusIcon,
} from "lucide-react";
import type { SignalConfig } from "./notifications.types";

// ── Signal type display config ────────────────────────────────────────────────

export const SIGNAL_TYPE_MAP: Record<string, SignalConfig> = {
  safety_alert: {
    level: "critical",
    Icon: ShieldAlertIcon,
    iconBg: "bg-coral-100 dark:bg-coral-500/20",
    iconColor: "text-coral-500",
    accentBar: "bg-coral-500",
    label: "Safety",
  },
  document_overdue: {
    level: "critical",
    Icon: FileWarningIcon,
    iconBg: "bg-coral-100 dark:bg-coral-500/20",
    iconColor: "text-coral-500",
    accentBar: "bg-coral-500",
    label: "Document",
  },
  crew_request: {
    level: "urgent",
    Icon: UserPlusIcon,
    iconBg: "bg-signal-100 dark:bg-signal-500/20",
    iconColor: "text-signal-600",
    accentBar: "bg-signal-500",
    label: "Crew",
  },
  payment_failed: {
    level: "urgent",
    Icon: CreditCardIcon,
    iconBg: "bg-signal-100 dark:bg-signal-500/20",
    iconColor: "text-signal-600",
    accentBar: "bg-signal-500",
    label: "Payment",
  },
  cert_expiring: {
    level: "urgent",
    Icon: ClockIcon,
    iconBg: "bg-signal-100 dark:bg-signal-500/20",
    iconColor: "text-signal-600",
    accentBar: "bg-signal-500",
    label: "Certificate",
  },
  expedition_update: {
    level: "info",
    Icon: CompassIcon,
    iconBg: "bg-abyss-50 dark:bg-abyss-800",
    iconColor: "text-abyss-500",
    accentBar: "bg-abyss-500",
    label: "Expedition",
  },
  route_change: {
    level: "info",
    Icon: MapIcon,
    iconBg: "bg-abyss-50 dark:bg-abyss-800",
    iconColor: "text-abyss-500",
    accentBar: "bg-abyss-500",
    label: "Route",
  },
  crew_confirmed: {
    level: "info",
    Icon: UserCheckIcon,
    iconBg: "bg-shallow-100 dark:bg-shallow-900",
    iconColor: "text-shallow-600",
    accentBar: "bg-shallow-500",
    label: "Crew",
  },
  payment_received: {
    level: "info",
    Icon: CheckCircle2Icon,
    iconBg: "bg-shallow-100 dark:bg-shallow-900",
    iconColor: "text-shallow-600",
    accentBar: "bg-shallow-500",
    label: "Payment",
  },
  certificate_issued: {
    level: "routine",
    Icon: AwardIcon,
    iconBg: "bg-violet-50 dark:bg-violet-900",
    iconColor: "text-violet-600",
    accentBar: "bg-violet-500",
    label: "Certificate",
  },
  system: {
    level: "routine",
    Icon: SettingsIcon,
    iconBg: "bg-slate-100 dark:bg-slate-800",
    iconColor: "text-slate-500",
    accentBar: "bg-slate-400",
    label: "System",
  },
};

const FALLBACK_CONFIG: SignalConfig = {
  level: "routine",
  Icon: BellIcon,
  iconBg: "bg-slate-100 dark:bg-slate-800",
  iconColor: "text-slate-500",
  accentBar: "bg-slate-400",
  label: "Signal",
};

export function getSignalConfig(type: string): SignalConfig {
  return SIGNAL_TYPE_MAP[type] ?? FALLBACK_CONFIG;
}

// ── Actionable signal types ────────────────────────────────────────────────────
// Types that require an explicit user action (accept/decline/retry/remind).
// Derived on the frontend because the backend Notification schema has no
// `actionable` field.

const ACTIONABLE_TYPES = new Set(["crew_request", "payment_failed", "cert_expiring"]);

export function isActionable(type: string): boolean {
  return ACTIONABLE_TYPES.has(type);
}
